import { Compass, Heart, Flower2, Baby, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const MotionLink = motion.create(Link);

const ETAPAS = [
  {
    id: "planejamento",
    slug: "planejamento",
    icon: Compass,
    title: "Planejamento",
    desc: "Informações para quem está pensando em ter filhos ou tentando engravidar.",
    temas: ["Fertilidade", "Reprodução assistida", "Exames", "Nutrição", "Saúde emocional"],
    img: "https://images.unsplash.com/photo-1668605335558-a13236f088e3?w=800&q=80&auto=format&fit=crop",
    alt: "Pessoa de costas pesquisando no laptop e anotando em um caderno",
    accent: {
      tag: "text-maternnia-gold",
      icon: "bg-maternnia-gold/10 text-maternnia-gold group-hover:bg-maternnia-gold",
      chip: "bg-maternnia-gold/10 text-maternnia-taupe",
      hoverBg: "hover:bg-[#FBF6EC]",
    },
  },
  {
    id: "gestacao",
    slug: "gestacao",
    icon: Heart,
    title: "Gestação",
    desc: "Conteúdos, checklists e profissionais para cada fase da gravidez.",
    temas: ["Obstetrícia", "Nutrição", "Fisioterapia pélvica", "Psicologia perinatal", "Preparação"],
    img: "https://static.prod-images.emergentagent.com/jobs/424cc6d9-f0bf-4897-9d6b-2170e84bc4d8/images/8d824a0405e7368852a125db122bee3666c4c82a2b40b4c0284606a6fb6f943a.png",
    alt: "Gestante negra acariciando a barriga em formato de coração",
    accent: {
      tag: "text-maternnia-pink",
      icon: "bg-maternnia-pink/10 text-maternnia-pink group-hover:bg-maternnia-pink",
      chip: "bg-maternnia-pink/10 text-maternnia-taupe",
      hoverBg: "hover:bg-maternnia-porcelana",
    },
  },
  {
    id: "parto",
    slug: "parto",
    icon: Flower2,
    portrait: true,
    title: "Parto",
    desc: "Orientação para entender opções, preparar decisões e montar sua rede de apoio.",
    temas: ["Tipos de parto", "Plano de parto", "Maternidades", "Doulas", "Cursos", "Equipe"],
    img: "https://customer-assets.emergentagent.com/job_pregnancy-platform/artifacts/zmire97u_file_00000000443471f4a54041f5a3a315c8.png",
    alt: "Mãe oriental sendo amparada ao chegar à maternidade",
    accent: {
      tag: "text-maternnia-eucalipto",
      icon: "bg-maternnia-eucalipto/10 text-maternnia-eucalipto group-hover:bg-maternnia-eucalipto",
      chip: "bg-maternnia-eucalipto/10 text-maternnia-taupe",
      hoverBg: "hover:bg-[#EEF3EF]",
    },
  },
  {
    id: "posparto",
    slug: "pos-parto",
    icon: Baby,
    title: "Pós-parto",
    desc: "Apoio para recuperação, amamentação, saúde emocional e primeiros cuidados.",
    temas: ["Pediatria", "Amamentação", "Puerpério", "Sono", "Fisioterapia", "Rede de apoio"],
    img: "https://static.prod-images.emergentagent.com/jobs/424cc6d9-f0bf-4897-9d6b-2170e84bc4d8/images/c12426409063711673535887afe4fea52b244d8a494bc8e3bfaafeb5e6e75b5f.png",
    alt: "Pezinhos de bebê pardo acolhidos pelas mãos de um adulto",
    accent: {
      tag: "text-maternnia-taupe",
      icon: "bg-maternnia-taupe/10 text-maternnia-taupe group-hover:bg-maternnia-taupe",
      chip: "bg-maternnia-taupe/10 text-maternnia-taupe",
      hoverBg: "hover:bg-maternnia-nevoa/40",
    },
  },
];

const Etapas = () => {
  return (
    <section id="etapas" data-testid="etapas-section" className="bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          className="max-w-2xl mb-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="text-sm font-semibold tracking-widest text-maternnia-pink uppercase">As 4 etapas</span>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-maternnia-charcoal mt-4 font-medium">
            Em que momento da jornada você está?
          </h2>
          <p className="mt-5 text-base md:text-lg text-maternnia-taupe leading-relaxed font-light">
            Escolha a etapa em que você se encontra e acesse conteúdos, checklists e profissionais selecionados para esse momento.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-7 lg:gap-8">
          {ETAPAS.map((etapa, i) => {
            const Icon = etapa.icon;
            return (
              <MotionLink
                key={etapa.id}
                to={`/${etapa.slug}`}
                data-testid={`etapa-card-${etapa.id}`}
                className={`group block overflow-hidden rounded-3xl bg-white border border-maternnia-nevoa/60 shadow-sm hover:shadow-[0_22px_50px_rgba(43,37,38,0.12)] hover:-translate-y-1 transition-all duration-300 ${etapa.accent.hoverBg}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-5">
                  <div className={`sm:col-span-2 relative overflow-hidden ${etapa.portrait ? "aspect-[4/5]" : "aspect-[4/3] sm:aspect-auto sm:min-h-[260px]"}`}>
                    <img
                      src={etapa.img}
                      alt={etapa.alt}
                      loading="lazy"
                      className={`absolute inset-0 w-full h-full object-cover ${etapa.objectPos || ""} transition-transform duration-500 group-hover:scale-105`}
                    />
                  </div>
                  <div className="sm:col-span-3 p-7 md:p-8 flex flex-col">
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`h-11 w-11 rounded-xl flex items-center justify-center transition-colors duration-300 ${etapa.accent.icon} [&>svg]:group-hover:text-white`}>
                        <Icon size={20} />
                      </span>
                      <span className={`text-xs font-semibold tracking-widest uppercase ${etapa.accent.tag}`}>
                        Etapa 0{i + 1}
                      </span>
                    </div>
                    <h3 className="font-serif text-2xl md:text-3xl text-maternnia-charcoal font-medium">{etapa.title}</h3>
                    <p className="mt-2 text-sm text-maternnia-taupe leading-relaxed font-light">{etapa.desc}</p>

                    <div className="flex flex-wrap gap-2 mt-5">
                      {etapa.temas.map((t) => (
                        <span key={t} className={`text-xs font-medium rounded-full px-3 py-1.5 ${etapa.accent.chip}`}>
                          {t}
                        </span>
                      ))}
                    </div>

                    <span className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-semibold text-maternnia-charcoal">
                      Explorar etapa
                      <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                    </span>
                  </div>
                </div>
              </MotionLink>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Etapas;
